/* eslint-disable no-mixed-spaces-and-tabs */
import React from "react";
import { useDrag } from "react-dnd";
import styled from "styled-components";
import { colourStaticGrey, colourNeutral3 } from "@ingka/variables/colours-css";
import Button from "@ingka/button";
import arrowLeftToLineSmall from "@ingka/ssr-icon/paths/arrow-left-to-line-small";
import arrowRightToLineSmall from "@ingka/ssr-icon/paths/arrow-right-to-line-small";
import { Tooltip } from "@mui/material";
import Text from "@ingka/text";

import { PaletteElement } from "../../core/api/paletteService";
import { DndItems } from "../../core/dnd";
import { EditorUISchemaElement } from "../../core/model/uischema";
import { StyledTreeItem, StyledTreeView } from "./Tree";

interface UiSchemaTreeItemProps {
	uiSchemaElementProvider: () => EditorUISchemaElement;
	type: string;
	label: string;
	icon?: React.ReactNode;
}

const Paragraph = styled.p`
	margin: 0;
	flex-grow: 1;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	margin-right: 1rem;
`;

const UiSchemaTreeItem: React.FC<UiSchemaTreeItemProps> = ({ uiSchemaElementProvider, type, label, icon }) => {
	const [{ isDragging }, drag] = useDrag({
		type: DndItems.newUISchemaElement(uiSchemaElementProvider()).type,
		item: DndItems.newUISchemaElement(uiSchemaElementProvider()),
		collect: (monitor) => ({
			isDragging: !!monitor.isDragging(),
		}),
	});

	return (
		<div ref={drag} data-cy={`${type}-source`}>
			<StyledTreeItem itemId={type} label={<Paragraph>{label}</Paragraph>} isDragging={isDragging} />
		</div>
	);
};

interface UIElementsTreeProps {
	className?: string;
	elements: PaletteElement[];
	hidePalettePanel: boolean;
	setHidePalettePanel: (hideLayoutPanel: boolean) => void;
}

const HeadingWrapper = styled.div`
	display: flex;
	padding-left: 1rem;
	padding-right: 0.5rem;
	margin-bottom: 0.5rem;
	border-bottom: 1px solid ${colourStaticGrey};
	justify-content: space-between;
	align-items: center;
`;

const StyledH2 = styled(Text)`
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
`;

export const UIElementsTree: React.FC<UIElementsTreeProps> = ({
	className,
	elements,
	hidePalettePanel,
	setHidePalettePanel,
}) => {
	return (
		<div>
			<HeadingWrapper>
				{!hidePalettePanel && (
					<StyledH2 data-testid="headerPropertyPanel" tagName="h2" textStyle="Heading.S">
						Layouts & Other
					</StyledH2>
				)}

				<div
					style={{
						borderLeft: hidePalettePanel ? "none" : `1px solid ${colourNeutral3}`,
					}}
				>
					<Tooltip
						title={hidePalettePanel ? "Show Palette Panel" : "Hide Palette Panel"}
						slotProps={{
							popper: {
								container: document.getElementById("main"),
							},
						}}
					>
						<Button
							data-testid="buttonResizePalettePanel"
							iconOnly
							size="small"
							type="tertiary"
							text={hidePalettePanel ? "Show Palette Panel" : "Hide Palette Panel"}
							onClick={() => setHidePalettePanel(!hidePalettePanel)}
							ssrIcon={!hidePalettePanel ? arrowLeftToLineSmall : arrowRightToLineSmall}
							style={{
								marginLeft: "0.5rem",
								...(hidePalettePanel
									? {
											position: "absolute",
											top: "0.5rem",
									  }
									: {}),
							}}
						/>
					</Tooltip>
				</div>
			</HeadingWrapper>

			{!hidePalettePanel && (
				<StyledTreeView defaultExpandedItems={[""]}>
					{elements.map(({ type, label, icon, uiSchemaElementProvider }) => (
						<UiSchemaTreeItem
							key={`treeitem-${type}`}
							type={type}
							label={label}
							icon={icon}
							uiSchemaElementProvider={uiSchemaElementProvider}
						/>
					))}
				</StyledTreeView>
			)}
		</div>
	);
};
